window.mx = window.mx || {};

window.mx.MxProductLookup = class extends window.mx.MxSection {
  constructor() {
    super();

    this.cachedSectionData = null;
    this.cachedOptions = new Map();
  }

  connectedCallback() {
    this.cacheSelectOptions(this.brandSelect);
    this.cacheSelectOptions(this.modelSelect);
    this.cacheSelectOptions(this.yearSelect);

    Array.from([this.brandSelect, this.modelSelect, this.yearSelect]).forEach((element) => {
      this.eventManager.addListener(element, 'change', () => this.onSelectChange(element));
    });
    this.submitButton.addEventListener('click', () => {
      this.goToLanding();
    });

    this.accessoryButtons.forEach((button) => {
      button.addEventListener('click', this.onAccessoryButtonClick);
    });

    this.initializeFilteredOptions();
  }

  get isAccessoryLookup() {
    return this.classList.contains('is-accessory-lookup');
  }
  get sectionData() {
    if (this.cachedSectionData === null) {
      const sectionId = this.section.id;
      this.cachedSectionData = JSON.parse(this.querySelector(`#${sectionId}__data`).textContent);
    }
    return this.cachedSectionData;
  }
  get brandSelect() {
    return this.querySelector('#brand-select');
  }
  get modelSelect() {
    return this.querySelector('#model-select');
  }
  get yearSelect() {
    return this.querySelector('#year-select');
  }
  get accessoryButtons() {
    return Array.from(this.querySelectorAll('.accessory-button-group button'));
  }
  get submitButton() {
    return this.querySelector('.submit-button');
  }
  get productCount() {
    return this.querySelector('.submit-button span');
  }
  get tagList() {
    if (!this.isAccessoryLookup) return [];

    const hasTagSelected = this.accessoryButtons.some((button) => !button.hasAttribute('data-excluded'));
    if (!hasTagSelected) {
      return [];
    }

    const tagList = [];
    this.accessoryButtons.forEach((button) => {
      if (!button.hasAttribute('data-excluded')) {
        tagList.push(button.dataset.tag);
      }
    });

    return tagList;
  }

  onAccessoryButtonClick = (event) => {
    const accessoryButton = event.currentTarget;
    const checkbox = accessoryButton.querySelector('input');

    if (accessoryButton.hasAttribute('data-excluded')) {
      accessoryButton.removeAttribute('data-excluded');
      checkbox.checked = true;
    } else {
      accessoryButton.setAttribute('data-excluded', 'true');
      checkbox.checked = false;
    }

    this.onSelectChange(this);
  };

  cacheSelectOptions(select) {
    const options = Array.from(select.options)
      .slice(1)
      .map((option) => ({
        value: option.value,
        text: option.textContent,
        element: option.cloneNode(true),
      }));
    this.cachedOptions.set(select.id, options);
  }

  onSelectChange(element) {
    try {
      this.submitButton.disabled = true;

      if (element.id === 'brand-select') {
        this.resetSelect(this.modelSelect);
        this.resetSelect(this.yearSelect);
      } else if (element.id === 'model-select') {
        this.resetSelect(this.yearSelect);

        const modelBrand = this.getModelBrand(this.modelSelect.value);
        if (modelBrand) {
          this.brandSelect.value = modelBrand;
        }
      }

      if (this.brandSelect.value === '' && this.modelSelect.value === '' && this.yearSelect.value === '') {
        this.resetSelect(this.brandSelect);
        this.resetSelect(this.modelSelect);
        this.resetSelect(this.yearSelect);
      }

      this.sectionData.brand = this.brandSelect.value;
      this.sectionData.model = this.modelSelect.value;
      this.sectionData.year = this.yearSelect.value;
      const { brand, model, year } = this.sectionData;

      if (brand !== '' && model === '' && year === '') {
        this.filterOptions(this.modelSelect, this.getBrandModelList(brand));
        this.filterOptions(this.yearSelect, this.getBrandYearList(brand));
      } else if (brand === '' && model === '' && year !== '') {
        this.filterOptions(this.modelSelect, this.getYearModelList(year));
      } else if (brand !== '' && model !== '' && year === '') {
        this.filterOptions(this.yearSelect, this.getModelYearList(model));
      } else if (brand !== '' && model === '' && year !== '') {
        this.filterOptions(this.modelSelect, this.getBrandYearModelList(brand, year));
      }

      const productCount = this.getProductCount(brand, model, year);

      if (brand === '' && model === '' && year === '') {
        const buttonLabel = this.sectionData.strings.button_label.initial_html;
        this.submitButton.innerHTML = buttonLabel.replace('[[count]]', productCount);
        this.submitButton.disabled = false;
      } else if (productCount === 0) {
        const buttonLabel = this.sectionData.strings.button_label.zero;
        this.submitButton.innerHTML = buttonLabel;
      } else {
        const buttonLabel =
          productCount === 1
            ? this.sectionData.strings.button_label.one
            : this.sectionData.strings.button_label.multiple_html;
        this.submitButton.innerHTML = buttonLabel.replace('[[count]]', productCount);

        this.submitButton.disabled = false;
      }
    } catch (error) {
      console.error('Error updating the select options', error);
      this.submitButton.disabled = false;
    }
  }

  filterOptions(select, optionList) {
    const firstOption = select.options[0];
    select.innerHTML = '';
    select.appendChild(firstOption);

    const cachedOptions = this.cachedOptions.get(select.id) || [];

    cachedOptions.forEach((cachedOption) => {
      if (optionList.includes(cachedOption.value)) {
        select.appendChild(cachedOption.element.cloneNode(true));
      }
    });
  }
  resetSelect(select) {
    const firstOption = select.options[0];
    select.innerHTML = '';
    select.appendChild(firstOption);

    const cachedOptions = this.cachedOptions.get(select.id) || [];
    cachedOptions.forEach((cachedOption) => {
      select.appendChild(cachedOption.element.cloneNode(true));
    });

    select.selectedIndex = 0;
  }

  getBrandProductList(brand) {
    let brandProductList = [];

    if (this.sectionData.products) {
      brandProductList = Object.values(this.sectionData.products).filter((product) => product.brand === brand);
    }

    return brandProductList;
  }
  getBrandModelList(brand) {
    let brandModelList = [];

    if (this.sectionData.brands[brand]) {
      const allBrandModels = this.sectionData.brands[brand].models;

      brandModelList = allBrandModels.filter((modelHandle) => {
        const modelProducts = this.getModelProductList(modelHandle);
        return modelProducts.length > 0;
      });
    }

    return brandModelList;
  }
  getBrandYearList(brand) {
    let brandYearList = [];

    const brandProductList = this.getBrandProductList(brand);

    if (brandProductList) {
      brandYearList = [
        ...new Set(
          brandProductList.reduce((accumulator, product) => {
            return accumulator.concat(product.years);
          }, []),
        ),
      ].sort();
    }

    return brandYearList;
  }
  getModelBrand(model) {
    let modelBrand = '';

    if (this.sectionData.models[model]) {
      modelBrand = this.sectionData.models[model].brand;
    }

    return modelBrand;
  }
  getModelProductList(model) {
    let modelProductList = [];

    if (this.sectionData.products) {
      modelProductList = Object.values(this.sectionData.products).filter((product) => product.model === model);
    }

    return modelProductList;
  }
  getModelYearList(model) {
    let modelYearList = [];

    const modelProductList = this.getModelProductList(model);
    if (modelProductList && modelProductList.length > 0) {
      modelYearList = [
        ...new Set(
          modelProductList.reduce((accumulator, product) => {
            return accumulator.concat(product.years);
          }, []),
        ),
      ];
    }

    return modelYearList;
  }
  getBrandYearModelList(brand, year) {
    let filteredModelList = [];

    if (this.sectionData.products) {
      filteredModelList = Object.values(this.sectionData.products)
        .filter((product) => {
          return product.brand === brand && product.years.includes(year);
        })
        .map((product) => product.model);
    }

    return filteredModelList;
  }
  getYearModelList(year) {
    let yearModelList = [];

    if (this.sectionData.products) {
      const yearProductList = Object.values(this.sectionData.products).filter((product) => {
        if (product.years) {
          return product.years.includes(year);
        }
      });

      yearModelList = yearProductList.map((product) => product.model);
    }

    return yearModelList;
  }
  getProductCount(brand, model, year) {
    if (!this.sectionData.products) {
      return 0;
    }

    return Object.values(this.sectionData.products).filter((product) => {
      const matchesBrand = brand === '' || product.brand === brand;
      const matchesModel = model === '' || product.model === model;
      const matchesYear = year === '' || (product.years && product.years.includes(year));
      const matchesTag = this.isAccessoryLookup
        ? this.tagList.length === 0 || this.tagList.includes(product.tag)
        : true;

      return matchesBrand && matchesModel && matchesYear && matchesTag;
    }).length;
  }
  goToLanding() {
    const { brands, models, products, collection_url, strings } = this.sectionData;

    const selectedBrand = this.brandSelect.value;
    const selectedModel = this.modelSelect.value;
    const selectedYear = this.yearSelect.value;

    const matchingProducts = Object.values(products).filter((product) => {
      const matchesBrand = !selectedBrand || product.brand === selectedBrand;
      const matchesModel = !selectedModel || product.model === selectedModel;
      const matchesYear = !selectedYear || (product.years && product.years.includes(selectedYear));
      const matchesTag = this.isAccessoryLookup
        ? this.tagList.length === 0 || this.tagList.includes(product.tag)
        : true;

      return matchesBrand && matchesModel && matchesYear && matchesTag;
    });

    if (matchingProducts.length === 0) {
      throw new Error('Aucun produit ne correspond à la sélection. Aucune redirection.');
    }

    if (matchingProducts.length === 1) {
      // Redirection vers la page produit
      const productHandle = matchingProducts[0].handle;
      window.location.href = `/products/${encodeURIComponent(productHandle)}`;
      return;
    }

    // Plusieurs résultats : construction de l'URL de collection avec filtres
    let url = collection_url || '/collections/all';
    const params = new URLSearchParams();

    if (selectedBrand && brands[selectedBrand]) {
      params.append(strings.param_name.brand, brands[selectedBrand].gid);
    }
    if (selectedModel && models[selectedModel]) {
      params.append(strings.param_name.model, models[selectedModel].gid);
    }
    if (selectedYear) {
      params.append(strings.param_name.year, selectedYear);
    }

    if (this.isAccessoryLookup && this.tagList.length > 0) {
      this.tagList.forEach((tag) => {
        const tagLabel = this.accessoryButtons.find((button) => button.dataset.tag === tag)?.dataset.label || tag;
        params.append(strings.param_name.tag, tagLabel);
      });
    }

    const paramString = params.toString();
    if (paramString) {
      url += (url.includes('?') ? '&' : '?') + paramString;
    }

    window.location.href = url;
  }

  initializeFilteredOptions = () => {
    this.filterBrandsWithoutProducts();
    this.filterModelsWithoutProducts();
    this.filterYearsWithoutProducts();
  };

  filterBrandsWithoutProducts = () => {
    const brandOptions = Array.from(this.brandSelect.options).slice(1);

    brandOptions.forEach((option) => {
      const brandHandle = option.value;
      let hasProductsForBrand = false;

      const brandModels = this.getBrandModelList(brandHandle);
      for (const modelHandle of brandModels) {
        const modelProducts = this.getModelProductList(modelHandle);
        if (modelProducts.length > 0) {
          hasProductsForBrand = true;
          break;
        }
      }

      if (!hasProductsForBrand) {
        option.remove();
      }
    });
  };

  filterModelsWithoutProducts = () => {
    const modelOptions = Array.from(this.modelSelect.options).slice(1);

    modelOptions.forEach((option) => {
      const modelHandle = option.value;
      const modelProducts = this.getModelProductList(modelHandle);

      if (modelProducts.length === 0) {
        option.remove();
      }
    });
  };

  filterYearsWithoutProducts = () => {
    const yearOptions = Array.from(this.yearSelect.options).slice(1);

    yearOptions.forEach((option) => {
      const year = option.value;
      let hasProductsForYear = false;

      if (this.sectionData.products) {
        const productsWithYear = Object.values(this.sectionData.products).filter((product) => {
          return product.years && product.years.includes(year);
        });
        hasProductsForYear = productsWithYear.length > 0;
      }

      if (!hasProductsForYear) {
        option.remove();
      }
    });
  };
};
if (!window.customElements.get('mx-product-lookup')) {
  window.customElements.define('mx-product-lookup', window.mx.MxProductLookup);
}
